import axios from 'axios'
import { getSessionApi } from './sessionApi.js'

// Browser half of the cookie session.
//
// New logins send `X-Session-Cookie: 1` so the API sets the httpOnly cookie.
// Older builds omit that header and stay on the bearer token. When Redis is down
// the API refuses the login; this module does not invent a token session for that.
// If the login succeeded but this origin cannot see the cookie (a bundled app on
// a site the browser treats as cross-site, so SameSite=Lax will not send it), the
// token from that successful login is stored so the shopper is not locked out.
//
// A browser that still has a token from before this change keeps sending it until
// logout or until /account/me reports `cookie: true`. A 200 that was authenticated
// by the bearer token is not that proof: the token stays, and one
// /controllers/account/session exchange is attempted.

export const SESSION_COOKIE_HEADER = 'X-Session-Cookie'

// Readable twin of the httpOnly session cookie, set by the API whenever a session
// is established (config/helpers/sessionHint.js). Keep this name in step with the
// server's `settings.vibe.sessionHintCookieName`; VITE_SESSION_HINT_COOKIE is how
// an environment that overrides it tells the storefront. Getting it wrong is not
// a correctness bug — the hint is simply never seen and every page load asks /me,
// which is what this module did before the cookie existed.
export const SESSION_HINT_COOKIE = import.meta.env?.VITE_SESSION_HINT_COOKIE || 'ihh_session'

/**
 * Whether the API has announced a session this browser could restore.
 * `null` when the cookie jar cannot be read at all, which is not the same answer
 * as "no session": the caller asks the API rather than guessing.
 */
export function hasSessionHintCookie() {
	if (typeof document === 'undefined' || typeof document.cookie !== 'string') return null
	for (const entry of document.cookie.split(';')) {
		const split = entry.indexOf('=')
		if (split < 0) continue
		if (entry.slice(0, split).trim() !== SESSION_HINT_COOKIE) continue
		// A cleared hint is sent as an empty value before the browser drops it.
		if (entry.slice(split + 1).trim()) return true
	}
	return false
}

export function sessionCookieHeaders(headers = {}) {
	return { ...headers, [SESSION_COOKIE_HEADER]: '1' }
}

export function sessionStoreErrorMessage(payload) {
	if (!payload || payload.code !== 'session_store_unavailable') return ''
	if (typeof payload.error === 'string' && payload.error) return payload.error
	if (Array.isArray(payload.errors) && payload.errors[0]) return String(payload.errors[0])
	if (typeof payload.message === 'string' && payload.message) return payload.message
	return 'Sign-in is temporarily unavailable. Please try again.'
}

const CLEARED_AUTH_COOKIE = 'Authorization=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'

function storage() {
	if (typeof window === 'undefined' || !window.localStorage) return null
	return window.localStorage
}

// localStorage only holds strings, so `setItem(key, undefined)` stores the
// literal "undefined". A returning visitor carrying one of these passes a plain
// truthiness check, and the member id then goes out as GET /members/undefined,
// which the API answers with a 401 (IHH-FRONT-NEW-EY). mango.js already screens
// the token this way; every identity read needs the same screen.
const POISONED_STORAGE_VALUES = new Set(['undefined', 'null', 'NaN'])

export function isPoisonedStorageValue(value) {
	return typeof value !== 'string' || value.trim() === '' || POISONED_STORAGE_VALUES.has(value.trim())
}

function readIdentity(key) {
	try {
		const value = storage()?.getItem(key)
		return isPoisonedStorageValue(value) ? '' : value
	} catch {
		return ''
	}
}

export function readStoredToken() {
	return readIdentity('token')
}

/** The stored member id, or '' when it is absent or was stored as "undefined". */
export function readStoredMemberId() {
	return readIdentity('user')
}

/**
 * Drop identity keys holding a poisoned value, so a browser that has carried one
 * since an older build stops sending it on every page load.
 */
export function purgePoisonedIdentity() {
	const local = storage()
	if (!local) return []
	const dropped = []
	for (const key of ['user', 'token']) {
		try {
			if (isPoisonedStorageValue(local.getItem(key)) && local.getItem(key) !== null) {
				local.removeItem(key)
				dropped.push(key)
			}
		} catch {
			// private mode
		}
	}
	return dropped
}

function rememberIdentity(memberId, email) {
	const local = storage()
	if (!local) return
	try {
		if (memberId) local.setItem('user', String(memberId))
		if (email) local.setItem('email', email)
	} catch {
		// private mode
	}
}

export function dropStoredBearerToken(http) {
	const local = storage()
	if (local) {
		try {
			local.removeItem('token')
			local.removeItem('auth')
		} catch {
			// private mode
		}
	}
	if (typeof document !== 'undefined') document.cookie = CLEARED_AUTH_COOKIE
	const common = http?.defaults?.headers?.common
	if (common) {
		delete common.Authorization
		delete common.authorization
	}
}

function persistBearerToken(token, http) {
	const local = storage()
	if (local) {
		try {
			local.setItem('token', token)
			local.setItem('auth', token)
		} catch {
			// private mode
		}
	}
	if (typeof document !== 'undefined') {
		const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString()
		document.cookie = `Authorization=${token};expires=${expires};path=/`
	}
	if (http?.defaults?.headers?.common) http.defaults.headers.common.Authorization = token
}

function isTransportFailure(error) {
	if (!error || typeof error !== 'object') return true
	const status = error.response?.status
	if (status === 401 || status === 403) return false
	if (!error.response) return true
	if (status === 0 || error.status === 0) return true
	if (status >= 500) return true
	return false
}

// The default axios Authorization header is whatever syncAuthHeader stored.
// It can make /me return 200 with no cookie at all. Only `cookie: true`
// (req.session.memberId on the server) means the httpOnly session is signed in.
function cookieConfirmed(data) {
	return data?.cookie === true
}

async function readMe(http, api) {
	try {
		const response = await http.get(`${api}/controllers/account/me`, { withCredentials: true, headers: sessionCookieHeaders() })
		const data = response?.data
		if (!data?.id) return { member: null, cookie: false }
		return { member: data, cookie: cookieConfirmed(data) }
	} catch (error) {
		if (isTransportFailure(error)) return { unavailable: true, cookie: false }
		return { member: null, cookie: false }
	}
}

function keepMember(member) {
	if (!member?.id) return null
	rememberIdentity(member.id, member.email)
	return member
}

/**
 * Restore the shopper. Drop the stored bearer token only after /account/me
 * reports `cookie: true`. A 200 that came from the token keeps the token and
 * tries the session exchange once; the token is dropped only if a later /me
 * confirms the cookie. Returns the member, `{ unavailable: true }` on a
 * network failure, or null.
 */
export async function restoreShopper({ axios: http = axios } = {}) {
	const api = getSessionApi()
	if (!api || typeof http?.get !== 'function') return null
	// A browser the API has never handed a session to has nothing to restore, and
	// asking anyway costs a blocking cross-origin round trip on every page load
	// whose answer is always 401 (p50 480ms, p95 1.8s; 88% of the calls to /me).
	// Stored identity still means ask: a browser that signed in before the hint
	// existed, or that is still on a bearer token, has a session this cookie does
	// not describe, and /me is what settles it. `null` from the hint means the jar
	// was unreadable, so ask then too.
	if (hasSessionHintCookie() === false && !readStoredMemberId() && !readStoredToken()) return null
	const first = await readMe(http, api)
	if (first.unavailable) return { unavailable: true }
	if (first.member?.id && first.cookie) {
		dropStoredBearerToken(http)
		return keepMember(first.member)
	}
	const token = readStoredToken()
	if (!token || typeof http.post !== 'function') return keepMember(first.member)
	let exchanged = null
	try {
		exchanged = await http.post(`${api}/controllers/account/session`, {}, {
			withCredentials: true,
			headers: sessionCookieHeaders({ Authorization: token }),
		})
	} catch (error) {
		if (!first.member?.id && isTransportFailure(error)) return { unavailable: true }
		return keepMember(first.member)
	}
	if (!exchanged?.data?.success) return keepMember(first.member)
	const second = await readMe(http, api)
	if (second.unavailable) {
		if (first.member?.id) return keepMember(first.member)
		return { unavailable: true }
	}
	const member = second.member?.id ? second.member : first.member
	if (!member?.id) return null
	if (second.cookie) dropStoredBearerToken(http)
	return keepMember(member)
}

/**
 * After a successful login that asked for a cookie (`X-Session-Cookie`).
 * The bearer token stays until /account/me reports `cookie: true`.
 */
export async function applyLoginSession({ memberId, email, token, axios: http = axios } = {}) {
	rememberIdentity(memberId, email)
	if (token) persistBearerToken(token, http)
	else dropStoredBearerToken(http)
	const api = getSessionApi()
	if (!api || typeof http?.get !== 'function') return { mode: token ? 'token-fallback' : 'cookie' }
	const me = await readMe(http, api)
	if (me.cookie) {
		dropStoredBearerToken(http)
		return { mode: 'cookie' }
	}
	if (me.unavailable || token) return { mode: 'token-fallback' }
	return { mode: 'cookie' }
}
